import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useAuthState } from "../hooks/useAuth";
import {
  getPublicLessonPath,
  getTutorialLessonSubject,
  isTutorialLesson,
} from "../constants/tutorialContent";
import "./PublicLessonsCarousel.css";

const LOGGED_IN_CAROUSEL_STORAGE_KEY = "tutorialLessonsCarouselHidden";
const VISITOR_CAROUSEL_STORAGE_KEY = "tutorialLessonsCarouselHiddenVisitor";
const LAST_CAROUSEL_ORDER_STORAGE_KEY = "publicLessonsCarouselLastOrder";

const getLessonKey = (lesson) => String(lesson?._id || "");

const readLastCarouselOrder = () => {
  try {
    const saved = localStorage.getItem(LAST_CAROUSEL_ORDER_STORAGE_KEY);
    const parsed = saved ? JSON.parse(saved) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveLastCarouselOrder = (lessons = []) => {
  try {
    localStorage.setItem(
      LAST_CAROUSEL_ORDER_STORAGE_KEY,
      JSON.stringify(lessons.map(getLessonKey).filter(Boolean)),
    );
  } catch {
    // Ignore storage failures; the carousel can still render a fresh shuffle.
  }
};

const readCarouselPreference = (storageKey, defaultHidden) => {
  const saved = localStorage.getItem(storageKey);
  return saved === null ? defaultHidden : saved === "true";
};

const hasSameOrder = (lessons = [], order = []) =>
  lessons.length === order.length &&
  lessons.every((lesson, index) => getLessonKey(lesson) === order[index]);

const rotateLessons = (lessons = []) => {
  if (lessons.length < 2) {
    return lessons;
  }

  const offset = Math.floor(Math.random() * (lessons.length - 1)) + 1;
  return [...lessons.slice(offset), ...lessons.slice(0, offset)];
};

const shuffleLessons = (lessons = [], previousOrder = []) => {
  const shuffled = [...lessons];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [
      shuffled[swapIndex],
      shuffled[index],
    ];
  }

  if (hasSameOrder(shuffled, previousOrder)) {
    return rotateLessons(shuffled);
  }

  return shuffled;
};

const buildCarouselLessons = (lessons = [], previousOrder = []) => {
  const tutorialLessons = lessons.filter(isTutorialLesson);
  const lessonsBySubject = tutorialLessons.reduce((groups, lesson) => {
    const subject = getTutorialLessonSubject(lesson);
    if (!groups[subject]) {
      groups[subject] = [];
    }
    groups[subject].push(lesson);
    return groups;
  }, {});

  const carouselLessons = tutorialLessons.map((lesson) => {
    const subject = getTutorialLessonSubject(lesson);
    const subjectLessons = lessonsBySubject[subject] || [];
    const subjectIndex = subjectLessons.findIndex(
      (subjectLesson) => subjectLesson._id === lesson._id,
    );

    return {
      ...lesson,
      carouselLabel: subject,
      carouselCounter:
        subjectLessons.length > 1
          ? `${subjectIndex + 1}/${subjectLessons.length}`
          : null,
    };
  });

  return shuffleLessons(carouselLessons, previousOrder);
};

const PublicLessonsCarousel = ({ lessons = [] }) => {
  const navigate = useNavigate();
  const { token, loading } = useAuthState();
  const isLoggedIn = Boolean(token);
  const storageKey = isLoggedIn
    ? LOGGED_IN_CAROUSEL_STORAGE_KEY
    : VISITOR_CAROUSEL_STORAGE_KEY;
  const defaultHidden = isLoggedIn;
  const [isCarouselHidden, setIsCarouselHidden] = React.useState(() => {
    const hasStoredToken = Boolean(localStorage.getItem("token"));
    return readCarouselPreference(
      hasStoredToken
        ? LOGGED_IN_CAROUSEL_STORAGE_KEY
        : VISITOR_CAROUSEL_STORAGE_KEY,
      hasStoredToken,
    );
  });
  const previousOrderRef = React.useRef(readLastCarouselOrder());

  const carouselLessons = React.useMemo(
    () => buildCarouselLessons(lessons, previousOrderRef.current),
    [lessons],
  );

  React.useEffect(() => {
    if (carouselLessons.length === 0) return;
    previousOrderRef.current = carouselLessons.map(getLessonKey);
    saveLastCarouselOrder(carouselLessons);
  }, [carouselLessons]);

  React.useEffect(() => {
    if (loading) return;
    setIsCarouselHidden(readCarouselPreference(storageKey, defaultHidden));
  }, [defaultHidden, loading, storageKey]);

  React.useEffect(() => {
    if (loading) return;
    localStorage.setItem(storageKey, isCarouselHidden);
  }, [isCarouselHidden, loading, storageKey]);

  if (carouselLessons.length === 0) {
    return null;
  }

  return (
    <>
      {!isCarouselHidden && (
        <div className="public-lessons-carousel-container">
          <button
            className="public-lessons-carousel-toggle"
            onClick={() => setIsCarouselHidden(true)}
            title="Hide tutorial lessons"
          >
            <ChevronLeft size={20} />
          </button>
          <div className="public-lessons-carousel">
            <div className="public-lessons-carousel-track">
              {carouselLessons.map((lesson, index) => (
                <button
                  key={`${lesson._id}-${index}`}
                  type="button"
                  className="public-lessons-carousel-card"
                  onClick={() => navigate(getPublicLessonPath(lesson))}
                  title={`${lesson.carouselLabel}${
                    lesson.author ? ` - ${lesson.author}` : ""
                  }${
                    lesson.carouselCounter ? `\n${lesson.carouselCounter}` : ""
                  }`}
                >
                  <span className="public-lessons-carousel-badges">
                    <span className="public-lessons-carousel-badge badge-lesson">
                      L
                    </span>
                    {lesson.hasNotes && (
                      <span className="public-lessons-carousel-badge badge-note">
                        N
                      </span>
                    )}
                    {lesson.hasFlashcards && (
                      <span className="public-lessons-carousel-badge badge-flashcard">
                        F
                      </span>
                    )}
                  </span>
                  <span className="public-lessons-carousel-name">
                    {lesson.carouselLabel}
                  </span>
                  {lesson.carouselCounter && (
                    <span className="public-lessons-carousel-counter">
                      {lesson.carouselCounter}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {isCarouselHidden && (
        <button
          className="public-lessons-carousel-show-button"
          onClick={() => setIsCarouselHidden(false)}
          title="Show tutorial lessons"
        >
          <ChevronRight size={20} />
        </button>
      )}
    </>
  );
};

export default PublicLessonsCarousel;
